// The bottom sheet's OPEN/CLOSE travel — the same mechanism the route transition uses for a Link slide.
//
// A CSS transition was the obvious way to do this and it is the wrong one on a phone, for two reasons the route
// engine already paid for:
//
//   • WHAT animates. Tailwind's `translate-y-*` drives the independent `translate` property; only
//     `transform: translate3d(…)` is reliably taken by the compositor across engines. The route engine animates
//     nothing else, and neither does this.
//   • WHEN it starts. A transition begins when base-ui flips `[data-starting-style]` off — a frame or two after
//     mount, landing on exactly the frames the menu's own commit, its avatars' decodes and the backdrop's first
//     blur are using. A Web Animations animation started in the layout effect of the mounting commit is handed
//     to the compositor before the first paint, with the whole trajectory known up front, so nothing after that
//     frame can starve it.
//
// So: integrate the spring ahead of time (see spring-travel.ts), write the start position pre-paint, hand the
// keyframes over, and take them back on arrival so the resting sheet carries no pinned inline styles.

import { useLayoutEffect, useRef, type RefObject } from 'react'
import { SPRING_OPTIONS, springValues } from '@/lib/spring-travel'
import { DISMISS_LANDED, dismissProgress } from '@/components/ui/drawer-snap-dismiss'
// The same release rule, on the same numbers, as a route swipe: one tuning for every gesture in the app.
import { TRANSITION_CONFIG } from '@/components/custom/route-transition/core/motion.config'

const { commitThreshold, flickVelocity } = TRANSITION_CONFIG

/** How raised the veil is right now, wherever it actually is — its resting dim comes from a CSS `calc()`. */
function backdropVeil(el: HTMLElement): number {
  return Number(getComputedStyle(el).opacity) || 0
}

/**
 * Where a travel has visually got to, in px of downward offset — read from the pixels rather than assumed, so
 * catching one mid-flight hands the next one the position on screen. This is the route engine's `interrupt`, for
 * the one case a sheet has: closed while it was still opening (or reopened while closing).
 */
function currentOffset(el: HTMLElement): number {
  const transform = getComputedStyle(el).transform
  if (transform === 'none') return 0
  try {
    return new DOMMatrixReadOnly(transform).f
  } catch {
    return 0
  }
}

function slide(el: HTMLElement, fromPx: number, toPx: number): Animation {
  return el.animate(
    springValues(fromPx, toPx).map((y) => ({ transform: `translate3d(0,${y}px,0)` })),
    SPRING_OPTIONS,
  )
}

/**
 * The veil: the backdrop's dim AND its blur, on one scalar so they arrive together.
 *
 * The blur has to be animated EXPLICITLY. `opacity` does not fade a `backdrop-filter` — WebKit paints the blur
 * at full strength however transparent the element is — so fading the element only ever faded the dim, and the
 * blur snapped on at the start of the travel. Unlike the sheet's own slide this one is not compositor-driven
 * (a backdrop filter is re-read from what's behind it), which is why it rides the same keyframes rather than
 * getting a clock of its own: if the main thread stalls, the blur lags a frame while the sheet keeps moving.
 */
function veil(el: HTMLElement, from: number, to: number, blurPx: number): Animation {
  return el.animate(
    springValues(from, to).map((level) => {
      const frame: Keyframe = { opacity: level.toFixed(3) }
      if (blurPx > 0) {
        const blur = `blur(${(Math.max(0, level) * blurPx).toFixed(2)}px)`
        frame.backdropFilter = blur
        frame.webkitBackdropFilter = blur
      }
      return frame
    }),
    SPRING_OPTIONS,
  )
}

/** The blur the backdrop rests at, in px — `0` where the engine has no backdrop-filter, so there's none to ramp. */
function restingBlur(el: HTMLElement): number {
  const style = getComputedStyle(el)
  const filter = style.getPropertyValue('backdrop-filter') || style.getPropertyValue('-webkit-backdrop-filter')
  return Number(/blur\(([\d.]+)px\)/.exec(filter)?.[1] ?? 0)
}

/**
 * Hold the backdrop at a given veil level across the first paint (see the play-pending note below).
 *
 * WHILE PINNED, THE CSS TRANSITION IS OFF. The backdrop carries a `transition-opacity` for the DRAG (see
 * drawer.tsx), and every value written here is one JS already knows the whole trajectory of — a travel's
 * keyframes, or a cursor's position. A transition on top of that has nothing to smooth and one thing to break:
 * it chases each write instead of tracking it, and it is what turned the hand-off at the end of a travel into a
 * blink (see releaseVeil).
 */
function pinVeil(el: HTMLElement, level: number, blurPx: number): void {
  el.style.transitionProperty = 'none'
  el.style.opacity = String(level)
  if (blurPx > 0) {
    const blur = `blur(${(level * blurPx).toFixed(2)}px)`
    el.style.setProperty('backdrop-filter', blur)
    el.style.setProperty('-webkit-backdrop-filter', blur)
  }
}

/**
 * Hand the backdrop back to CSS: its resting dim and blur are the stylesheet's, and the dim rides the drag.
 *
 * ACROSS ONE SUPPRESSED STYLE CHANGE, or the sheet blinks the instant it arrives. The value being handed back
 * FROM is the one this travel PINNED at its start — 0 for an open — so simply dropping the pin reads as
 * "opacity 0 → 1" at the next style recalc and starts the backdrop's own 100ms transition from zero. Measured
 * in-browser: after the hand-off the computed opacity was `0` with a `CSSTransition` running, i.e. the veil
 * dropped out and faded back in at exactly the frame the travel finished. And the dim is not the only thing
 * that goes: Chromium scales a `backdrop-filter` by its element's opacity, so the BLUR blinked with it.
 *
 * Flushing the released style while the transition is still off makes the RESTING value the before-change
 * style, so nothing has anywhere to transition from and the resting sheet still gets its CSS transition back
 * for the drag.
 */
function releaseVeil(el: HTMLElement): void {
  el.style.removeProperty('opacity')
  el.style.removeProperty('backdrop-filter')
  el.style.removeProperty('-webkit-backdrop-filter')
  // The style change event that would otherwise start the transition, taken here — while `transition-property`
  // is still the `none` pinVeil wrote.
  void getComputedStyle(el).opacity
  el.style.removeProperty('transition-property')
}

/** Motion the user has asked not to see. The sheet still opens and closes — it just arrives. */
function motionSuppressed(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Take the sheet back from whatever is animating it, freezing it where it visually IS. Both halves have to be
 * caught: cancelling an animation reverts the element to the style underneath it, which for the veil is the level
 * that travel started at — the dim would snap back before the sheet had gone anywhere. Returns the caught offset,
 * or `null` if nothing was moving.
 */
function catchTravel(
  animations: readonly Animation[],
  scroller: HTMLElement,
  backdrop: HTMLElement | null,
  blurPx: number,
): number | null {
  const moving = animations.some((animation) => animation.playState === 'running')
  const offset = moving ? currentOffset(scroller) : null
  const level = moving && backdrop ? backdropVeil(backdrop) : null
  for (const animation of animations) animation.cancel()
  if (offset !== null) scroller.style.transform = `translate3d(0,${offset}px,0)`
  if (level !== null && backdrop) pinVeil(backdrop, level, blurPx)
  return offset
}

interface SheetTravelArgs {
  /** The fixed scroll-snap container. The WHOLE sheet travels with it, so nothing inside it is transformed. */
  scrollerRef: RefObject<HTMLElement | null>
  backdropRef: RefObject<HTMLElement | null>
  /** The visible sheet, whose height is what a drag is measured against. */
  panelRef: RefObject<HTMLElement | null>
  /** The grab strip. A MOUSE drag on it is the only way to pull the sheet down without a native scroll. */
  handleRef: RefObject<HTMLElement | null>
  /** The sheet's own open state — `false` while base-ui keeps it mounted for the exit. */
  open: boolean
  onDismiss: () => void
}

export function useSheetTravel(args: SheetTravelArgs): void {
  const { scrollerRef, backdropRef, panelRef, handleRef, open, onDismiss } = args
  /** Where an interrupted travel was caught, for the next one to start from. */
  const caughtAt = useRef<number | null>(null)
  /** The backdrop's resting blur, cached the first time it is read — after that, the element carries a pinned
   *  value from a travel and reading it back would mistake THAT for the target. */
  const restingBlurPx = useRef<number | null>(null)
  /** Whatever currently drives the sheet, from either path, so each can take it from the other. */
  const driving = useRef<Animation[]>([])

  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    const backdrop = backdropRef.current
    // No `Element.animate` (the same floor the route engine keeps a fallback for): the sheet appears at rest
    // rather than animating from a position nothing can move it away from.
    if (!scroller || typeof scroller.animate !== 'function' || motionSuppressed()) return

    // One layout read per travel, like the route engine's one rect read per press. The scroller spans the
    // viewport plus the bleed, so its own height is the distance that takes it fully off screen.
    const distance = scroller.offsetHeight
    const animations: Animation[] = []
    // Reopened mid-close (or closed mid-open): carry on from where the caught travel actually was.
    const from = caughtAt.current
    caughtAt.current = null

    if (backdrop && restingBlurPx.current === null) restingBlurPx.current = restingBlur(backdrop)
    const blurPx = restingBlurPx.current ?? 0

    if (open) {
      // Write the start state BEFORE the first paint: a just-created animation is play-pending, and until the
      // browser assigns its start time it contributes nothing — the sheet would paint once at rest and only then
      // jump off screen to travel back in.
      const startAt = from ?? distance
      const startVeil = backdrop && from !== null ? backdropVeil(backdrop) : 0
      scroller.style.transform = `translate3d(0,${startAt}px,0)`
      if (backdrop) pinVeil(backdrop, startVeil, blurPx)
      animations.push(slide(scroller, startAt, 0))
      if (backdrop) animations.push(veil(backdrop, startVeil, 1, blurPx))
      void Promise.all(animations.map((animation) => animation.finished))
        .then(() => {
          // Hand the sheet back to CSS on arrival: nothing may stay pinned on a resting sheet — the backdrop's
          // dim belongs to the drag from here on (it rides `--sheet-dismiss`), and a `fill: forwards` transform
          // would hold the panel wherever it landed forever.
          for (const animation of animations) animation.cancel()
          scroller.style.transform = ''
          if (backdrop) releaseVeil(backdrop)
        })
        .catch(() => {
          // A cancel (this effect tearing down, the sheet closing mid-travel) rejects `finished`. That IS the
          // interruptible path — the exit branch below takes over from wherever it got to.
        })
    } else {
      // Dismissed by the FINGER: the scroll already carried the sheet off screen, so there is nothing to
      // travel — and lifting the veil from full would flash the dim back before it left.
      const alreadyGone = dismissProgress(scroller.scrollTop, scroller.scrollHeight - scroller.clientHeight)
      if (alreadyGone >= DISMISS_LANDED) return
      animations.push(slide(scroller, from ?? 0, distance))
      // From wherever the veil actually is, so closing mid-drag (or mid-open) doesn't jump.
      if (backdrop) animations.push(veil(backdrop, backdropVeil(backdrop), 0, blurPx))
      // Nothing to clean up on arrival: base-ui waits for these (it awaits `getAnimations()`) and then unmounts.
    }

    driving.current = animations
    return () => {
      // Catch what is still travelling, so the next travel (the sheet closed while it was opening, or reopened
      // while closing) starts from the pixels on screen rather than from an endpoint.
      caughtAt.current = catchTravel(animations, scroller, backdrop, blurPx)
      driving.current = []
    }
  }, [open, scrollerRef, backdropRef])

  // A MOUSE drag on the handle. The dismiss gesture is a native scroll, which a mouse cannot perform — press and
  // drag moves no scroller — so on a narrow DESKTOP window the handle was inert: the sheet could only be closed
  // by the backdrop, Escape, or a wheel. This drives the same transform the travel animates, with the same spring
  // on release, and it can catch a travel that is still running (grab the handle mid-open and it stops under the
  // cursor). Touch is deliberately excluded: there the native scroll already owns the gesture, and two drivers on
  // one sheet would fight.
  useLayoutEffect(() => {
    const handle = handleRef.current
    const scroller = scrollerRef.current
    if (!handle || !scroller) return

    let dragging = false
    let pointerId: number | null = null
    let startY = 0
    let baseOffset = 0
    let offset = 0
    let lastY = 0
    let lastT = 0
    let velocity = 0
    let panelHeight = 0

    const backdrop = () => backdropRef.current
    const blur = () => restingBlurPx.current ?? 0

    const paint = (): void => {
      scroller.style.transform = `translate3d(0,${offset}px,0)`
      const layer = backdrop()
      if (layer) pinVeil(layer, panelHeight > 0 ? Math.max(0, 1 - offset / panelHeight) : 1, blur())
    }

    const onPointerDown = (event: PointerEvent): void => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || dragging) return
      const panel = panelRef.current
      const layer = backdrop()
      panelHeight = panel ? panel.offsetHeight : scroller.offsetHeight
      if (layer && restingBlurPx.current === null) restingBlurPx.current = restingBlur(layer)
      // Catch a travel in flight so the sheet stops under the cursor instead of jumping to an endpoint.
      baseOffset = catchTravel(driving.current, scroller, layer, blur()) ?? currentOffset(scroller)
      driving.current = []
      dragging = true
      pointerId = event.pointerId
      startY = event.clientY
      lastY = event.clientY
      lastT = event.timeStamp
      velocity = 0
      offset = baseOffset
      // Otherwise the press starts a text selection that swallows the pointer stream mid-drag.
      event.preventDefault()
      document.body.style.userSelect = 'none'
    }

    const onPointerMove = (event: PointerEvent): void => {
      if (!dragging || event.pointerId !== pointerId) return
      const dt = event.timeStamp - lastT
      if (dt > 0) velocity = velocity * 0.6 + ((event.clientY - lastY) / dt) * 0.4
      lastY = event.clientY
      lastT = event.timeStamp
      // Down only: there is nothing above the resting position to drag into.
      offset = Math.max(0, baseOffset + (event.clientY - startY))
      paint()
    }

    const end = (): void => {
      if (!dragging) return
      dragging = false
      pointerId = null
      document.body.style.userSelect = ''
      // The route engine's release rule, on its constants: a flick decides regardless of distance, otherwise the
      // sheet goes wherever it is more than `commitThreshold` of the way to.
      const flicked = velocity > flickVelocity ? true : velocity < -flickVelocity ? false : undefined
      const dismissing = flicked ?? offset / Math.max(1, panelHeight) > commitThreshold
      if (dismissing) {
        // Hand the endpoint to the exit travel rather than animating here, so there is exactly one spring.
        caughtAt.current = offset
        onDismiss()
        return
      }
      const layer = backdrop()
      // Dragging is direct manipulation, so it stands even under reduced motion — but the release must not
      // animate there; it just puts the sheet back.
      if (motionSuppressed() || typeof scroller.animate !== 'function') {
        scroller.style.transform = ''
        if (layer) releaseVeil(layer)
        return
      }
      const springBack = [slide(scroller, offset, 0)]
      if (layer) springBack.push(veil(layer, backdropVeil(layer), 1, blur()))
      driving.current = springBack
      void Promise.all(springBack.map((animation) => animation.finished))
        .then(() => {
          for (const animation of springBack) animation.cancel()
          driving.current = []
          scroller.style.transform = ''
          if (layer) releaseVeil(layer)
        })
        .catch(() => {
          // Caught again mid-spring-back, or the sheet closed. Whoever cancelled it owns the sheet now.
        })
    }

    handle.addEventListener('pointerdown', onPointerDown)
    // On WINDOW, not the handle: a fast drag leaves the 20px strip immediately, and a release outside it would
    // otherwise never arrive — leaving the sheet stuck under a cursor that isn't pressed any more.
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
    window.addEventListener('blur', end)
    return () => {
      handle.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
      window.removeEventListener('blur', end)
      if (dragging) document.body.style.userSelect = ''
    }
  }, [handleRef, scrollerRef, backdropRef, panelRef, onDismiss])
}
